import React from 'react';

interface LoadingSpinnerProps {
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  color?: 'primary' | 'white' | 'secondary';
}

/**
 * Unified Loading Spinner Component
 *
 * Professional, minimalistic spinner with consistent sizing across the application.
 * Uses border animation for smooth, industry-standard loading indicator.
 *
 * @example
 * ```tsx
 * <LoadingSpinner size="md" color="primary" />
 * <LoadingSpinner size="sm" color="white" />
 * ```
 */
export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  className = '',
  color = 'primary'
}) => {
  const sizeClasses = {
    xs: 'h-3 w-3 border',
    sm: 'h-4 w-4 border-2',
    md: 'h-6 w-6 border-2',
    lg: 'h-8 w-8 border-2',
    xl: 'h-12 w-12 border-4',
  };

  // Monochrome-first: the default "primary" spinner is near-black / white (no brand blue).
  const colorClasses = {
    primary: 'border-secondary-900 border-t-transparent dark:border-white',
    white: 'border-white border-t-transparent',
    secondary: 'border-secondary-400 border-t-transparent dark:border-secondary-500',
  };

  return (
    <div
      className={`
        animate-spin rounded-full
        ${sizeClasses[size]}
        ${colorClasses[color]}
        ${className}
      `}
      role="status"
      aria-label="Loading"
    >
      <span className="sr-only">Loading...</span>
    </div>
  );
};

export default LoadingSpinner;
